import { DestroyRef, Injectable, Inject, LOCALE_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { auditTime, filter } from 'rxjs/operators';

import {
  Breadcrumb,
  DatabaseCacheService,
  Section,
} from '../../../data/cache/database-cache.service';
import { PaginationService } from '@app/core/http/pagination.service';
import { ScrollService } from '@app/core/layout/scroll.service';
import { UserRecordListStateService } from 'src/app/services/user-record-list-state.service';
import { RecordData, RecordTypes, TypesUtils } from '../../../types';
import { MediaDocument } from '@app/data/models/media/media.model';
import {
  BaseViewModel,
  BaseViewModelState,
} from '../../../shared/view-model/base-view-model';
import { USER_RECORD_LIST_I18N } from './user-record-list.i18n';
import { getTimestamp as getDateTimestamp } from '@app/core/date/date-time.util';
import {
  buildRecordTypeSearchText,
  getRecordTypePrimaryLabel,
  getRecordTypeSecondaryLabel,
  normalizeRecordType,
} from './record-type-display.util';
import { UnifiedRow } from './unified-row.model';

export type SortDirection = 'asc' | 'desc' | '';

export interface ColumnDefinition {
  def: string;
  label: string;
  visible: boolean;
  /** Columns that cannot be hidden (e.g. createdAt, actions) */
  locked?: boolean;
}

export interface ProtocolFilterOption {
  code: string;
  label: string;
  secondaryLabel: string;
  tooltip: string;
}

export interface UserRecordListState extends BaseViewModelState {
  breadcrumbs: Breadcrumb[];
  section: Section | null;
  selectedUserWithUserData: any;
  rows: UnifiedRow[];
  filteredRows: UnifiedRow[];
  pagedRows: UnifiedRow[];
  filterText: string;
  pageIndex: number;
  pageSize: number;
  sortActive: string;
  sortDirection: SortDirection;
  scrollTop: number;
  isAnonymous: boolean;
  columnDefinitions: ColumnDefinition[];
  displayedColumns: string[];
  protocolFilterOptions: ProtocolFilterOption[];
  selectedProtocolFilters: string[];
}

const initialState: UserRecordListState = {
  status: 'idle',
  errorMessage: null,
  breadcrumbs: [],
  section: null,
  selectedUserWithUserData: null,
  rows: [],
  filteredRows: [],
  pagedRows: [],
  filterText: '',
  pageIndex: 0,
  pageSize: 10,
  sortActive: 'createdAt',
  sortDirection: 'desc',
  scrollTop: 0,
  isAnonymous: false,
  columnDefinitions: [],
  displayedColumns: [],
  protocolFilterOptions: [],
  selectedProtocolFilters: [],
};

@Injectable()
export class UserRecordListViewModel extends BaseViewModel<UserRecordListState> {
  private userId: string | null = null;
  private allRows: UnifiedRow[] = [];
  private recordTypeFilter: string[] | null = null;
  private readonly isEnglish: boolean;
  private readonly protocolLocale: 'ja' | 'en' | 'zh-TW';

  constructor(
    private readonly common: DatabaseCacheService,
    private readonly listStateService: UserRecordListStateService,
    private readonly paginationService: PaginationService,
    private readonly scrollService: ScrollService,
    private readonly destroyRef: DestroyRef,
    @Inject(LOCALE_ID) localeId: string,
  ) {
    super(initialState);
    const lower = (localeId || '').toLowerCase();
    this.isEnglish = lower.startsWith('en');
    this.protocolLocale = lower.startsWith('en') ? 'en' : lower.startsWith('zh') ? 'zh-TW' : 'ja';

    this.common.parametersUpdated
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(e =>
          e.type === 'allDataUpdated' ||
          e.type === 'selectedUserChanged' ||
          ((e.type === 'recordsUpdated' || e.type === 'mediasUpdated') &&
            e.userId === this.userId)
        ),
        auditTime(0),
      )
      .subscribe(() => this.refreshFromCache());
  }

  init(userId: string | null, recordTypeFilter: string[] | null = null): void {
    if (!userId) {
      return;
    }

    const normalized = userId;
    const normalizedFilter = this.normalizeRecordTypeFilter(recordTypeFilter);
    const hasChanged = normalized !== this.userId;
    const filterChanged = !this.isSameFilter(this.recordTypeFilter, normalizedFilter);
    this.userId = normalized;
    this.recordTypeFilter = normalizedFilter;
    this.restorePersistedState();

    if (hasChanged || filterChanged) {
      this.setLoading();
      this.allRows = [];
    }

    this.common.prepareUserRecordsList(normalized, false);
    this.common.prepareUserMediasList(normalized, false);
  }

  updateFilter(text: string): void {
    this.persistFilterText(text);
    this.patchState({ filterText: text, pageIndex: 0 });
    this.recalculateRows();
  }

  updatePagination(pageIndex: number, pageSize: number): void {
    if (!this.userId) {
      return;
    }
    this.paginationService.setPageIndex(this.userId, pageIndex);
    this.paginationService.setPageSize(this.userId, pageSize);
    this.patchState({ pageIndex, pageSize });
    this.recalculatePagedRows();
  }

  updateSort(active: string, direction: SortDirection): void {
    this.patchState({
      sortActive: active,
      sortDirection: direction,
    });
    this.recalculateRows();
  }

  toggleColumnVisibility(columnDef: string): void {
    const definitions = this.currentState.columnDefinitions.map(cd =>
      cd.def === columnDef && !cd.locked
        ? { ...cd, visible: !cd.visible }
        : cd
    );
    const displayedColumns = definitions
      .filter(cd => cd.visible)
      .map(cd => cd.def);
    this.persistColumnVisibility(definitions);
    this.patchState({ columnDefinitions: definitions, displayedColumns });
  }

  toggleProtocolFilter(recordType: string, selected: boolean): void {
    const normalized = this.getProtocolFilterKey(recordType);
    if (!normalized) {
      return;
    }

    const available = this.currentState.protocolFilterOptions.map((option) => option.code);
    const availableKeys = new Set(
      available
        .map((code) => this.getProtocolFilterKey(code))
        .filter((code) => code.length > 0),
    );
    if (!availableKeys.has(normalized)) {
      return;
    }

    const selectedSet = new Set(
      this.currentState.selectedProtocolFilters
        .map((code) => this.getProtocolFilterKey(code))
        .filter((code) => code.length > 0),
    );
    if (selected) {
      selectedSet.add(normalized);
    } else {
      selectedSet.delete(normalized);
    }

    const nextSelection = available.filter((code) =>
      selectedSet.has(this.getProtocolFilterKey(code)),
    );
    this.applyProtocolFilterSelection(nextSelection);
  }

  selectAllProtocolFilters(): void {
    this.applyProtocolFilterSelection(
      this.currentState.protocolFilterOptions.map((option) => option.code),
    );
  }

  clearAllProtocolFilters(): void {
    this.applyProtocolFilterSelection([]);
  }

  refresh(): void {
    if (!this.userId) {
      return;
    }
    this.setLoading();
    this.common.prepareUserRecordsList(this.userId, true);
    this.common.prepareUserMediasList(this.userId, true);
  }

  updateScrollPosition(scrollTop: number): void {
    if (this.userId) {
      this.scrollService.setScrollPosition(this.userId, scrollTop);
    }
    this.patchState({ scrollTop });
  }

  private refreshFromCache(): void {
    if (!this.userId) {
      return;
    }

    const selectedSection = this.common.getSelectedSection() ?? null;
    const selectedUser =
      this.common.getSelectedUserWithUserData(this.userId) ?? null;
    const breadcrumbs = selectedSection
      ? [
          {
            label: '',
            icon: 'home',
            link: ['/home', selectedSection.sectionId],
          },
          {
            label: USER_RECORD_LIST_I18N.breadcrumb.userList,
            icon: '',
            link: ['/home', selectedSection.sectionId, 'users'],
          },
          { label: USER_RECORD_LIST_I18N.breadcrumb.recordList },
        ]
      : [];

    const rawRecords = this.common.getUserRecordsList();
    if (!rawRecords) {
      this.allRows = [];
      this.patchState({
        status: 'loading',
        breadcrumbs,
        section: selectedSection,
        selectedUserWithUserData: selectedUser,
        isAnonymous: this.userId === 'ANONYMOUS',
        rows: [],
        filteredRows: [],
        pagedRows: [],
        protocolFilterOptions: [],
        selectedProtocolFilters: [],
      });
      return;
    }

    const rawMedias: any[] | null = this.common.getUserMediasList();
    if (rawMedias === null) {
      this.allRows = [];
      this.patchState({
        status: 'loading',
        breadcrumbs,
        section: selectedSection,
        selectedUserWithUserData: selectedUser,
        isAnonymous: this.userId === 'ANONYMOUS',
        rows: [],
        filteredRows: [],
        pagedRows: [],
        protocolFilterOptions: [],
        selectedProtocolFilters: [],
      });
      return;
    }

    const converted = TypesUtils.convertJsonToRecords(rawRecords, true);
    const filteredRecords = this.filterRowsByRecordType(converted.records);
    const typedMedias: MediaDocument[] = rawMedias;
    this.allRows = this.mergeRecordsAndMedias(filteredRecords, rawMedias, typedMedias);
    const protocolFilterOptions = this.buildProtocolFilterOptions(this.allRows);
    const selectedProtocolFilters = this.resolveSelectedProtocolFilters(
      protocolFilterOptions.map((option) => option.code),
    );

    const isAnonymous = this.userId === 'ANONYMOUS';
    const columnDefinitions = this.buildColumnDefinitions(isAnonymous);
    const displayedColumns = columnDefinitions
      .filter(cd => cd.visible)
      .map(cd => cd.def);

    const { filterText, sortActive, sortDirection, pageSize } = this.currentState;
    const pageIndex = this.currentState.pageIndex;
    const sorted = this.sortRows(
      this.filterRows(this.allRows, filterText, selectedProtocolFilters),
      sortActive,
      sortDirection,
    );
    const safePageIndex = Math.min(
      pageIndex,
      Math.max(Math.ceil(sorted.length / pageSize) - 1, 0),
    );
    const pagedRows = sorted.slice(safePageIndex * pageSize, safePageIndex * pageSize + pageSize);

    this.patchState({
      status: 'success',
      breadcrumbs,
      section: selectedSection,
      selectedUserWithUserData: selectedUser,
      isAnonymous,
      columnDefinitions,
      displayedColumns,
      protocolFilterOptions,
      selectedProtocolFilters,
      rows: this.allRows,
      filteredRows: sorted,
      pageIndex: safePageIndex,
      pagedRows,
    });
  }

  private restorePersistedState(): void {
    if (!this.userId) {
      return;
    }
    const filterText =
      this.listStateService.getFilterText(this.userId) ??
      initialState.filterText;
    const pageIndex =
      this.paginationService.getPageIndex(this.userId) ??
      initialState.pageIndex;
    const pageSize =
      this.paginationService.getPageSize(this.userId) ?? initialState.pageSize;
    const scrollTop =
      this.scrollService.getScrollPosition(this.userId) ??
      initialState.scrollTop;
    this.patchState({ filterText, pageIndex, pageSize, scrollTop });
  }

  private recalculateRows(): void {
    const {
      filterText,
      selectedProtocolFilters,
      sortActive,
      sortDirection,
      pageIndex,
      pageSize,
    } =
      this.currentState;
    const filtered = this.filterRows(
      this.allRows,
      filterText,
      selectedProtocolFilters,
    );
    const sorted = this.sortRows(filtered, sortActive, sortDirection);
    const safePageIndex = Math.min(
      pageIndex,
      Math.max(Math.ceil(sorted.length / pageSize) - 1, 0),
    );
    this.patchState({
      filteredRows: sorted,
      pageIndex: safePageIndex,
    });
    this.recalculatePagedRows();
  }

  private recalculatePagedRows(): void {
    const { filteredRows, pageIndex, pageSize } = this.currentState;
    const start = pageIndex * pageSize;
    const paged = filteredRows.slice(start, start + pageSize);
    this.patchState({ pagedRows: paged });
  }

  private filterRows(
    rows: UnifiedRow[],
    filterText: string,
    selectedProtocolFilters: string[],
  ): UnifiedRow[] {
    const protocolFiltered = this.applyProtocolFilters(rows, selectedProtocolFilters);
    const term = filterText.trim().toLowerCase();
    if (!term) {
      return protocolFiltered;
    }
    return protocolFiltered.filter(row => this.matchesFilter(row, term));
  }

  private matchesFilter(row: UnifiedRow, term: string): boolean {
    const includes = (value?: string | null): boolean =>
      !!value && value.toLowerCase().includes(term);

    if (row.kind === 'media') {
      const m = row.media;
      return (
        includes(m?.data?.method) ||
        includes(m?.data?.comment) ||
        includes(m?.data?.createdAt) ||
        includes(row.id)
      );
    }

    const rec = row.record!;
    const measurer = rec.meta?.measurer;
    if (
      includes(measurer?.userId) ||
      includes(measurer?.lastName) ||
      includes(measurer?.firstName)
    ) {
      return true;
    }

    const user = rec.meta?.user;
    if (this.currentState.isAnonymous) {
      if (!user?.code) {
        if (includes(user?.lastName) || includes(user?.firstName)) {
          return true;
        }
      } else if (includes(user.code)) {
        return true;
      }
    }

    const data = rec.data ?? {};
    const recordTypeSearchText = buildRecordTypeSearchText(
      data.recordType,
      data.recordTypeView,
    );
    if (
      includes(data.createdAtLocalStr) ||
      includes(data.createdAtLocationStr) ||
      (data.testDuration != null &&
        String(data.testDuration).toLowerCase() === term) ||
      includes(recordTypeSearchText)
    ) {
      return true;
    }

    if (includes(rec.id)) {
      return true;
    }

    return false;
  }

  private sortRows(
    rows: UnifiedRow[],
    active: string,
    direction: SortDirection,
  ): UnifiedRow[] {
    if (!direction) {
      return rows.slice();
    }
    const sorted = rows.slice().sort((a, b) => {
      const aValue = this.getSortableValue(a, active);
      const bValue = this.getSortableValue(b, active);
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return aValue - bValue;
      }
      return String(aValue).localeCompare(String(bValue));
    });
    return direction === 'asc' ? sorted : sorted.reverse();
  }

  private getSortableValue(row: UnifiedRow, active: string): string | number {
    if (row.kind === 'media') {
      switch (active) {
        case 'createdAt':
          return this.getTimestamp(row.media?.data?.createdAt) ?? 0;
        case 'recordType':
          return row.media?.data?.method ?? '';
        default:
          return 0;
      }
    }

    const rec = row.record!;
    switch (active) {
      case 'createdAt':
        return this.getTimestamp(rec.data?.createdAt) ?? 0;
      case 'recordType':
        return getRecordTypePrimaryLabel(
          rec.data?.recordType,
          rec.data?.recordTypeView,
          this.protocolLocale,
        );
      case 'userName':
        return rec.meta?.user?.displayName ?? '';
      case 'measurerName':
        return `${rec.meta?.measurer?.lastName ?? ''} ${
          rec.meta?.measurer?.firstName ?? ''
        }`.trim();
      case 'numOfSwallowing':
        return rec.data?.numOfSwallowings ?? 0;
      case 'coughPresence':
        return this.toCoughPresenceSortValue(rec.data?.numOfCoughs);
      case 'meanSwallowingTime':
        return rec.data?.swallowingsTimeAverage ?? 0;
      case 'sd':
        return rec.data?.swallowingsTimeVariance ?? 0;
      case 'wavDuration':
        return rec.data?.wavDuration ?? 0;
      case 'id':
        return rec.id ?? '';
      default:
        return 0;
    }
  }

  private getTimestamp(value?: string | null): number | null {
    return getDateTimestamp(value);
  }

  private toCoughPresenceSortValue(value: unknown): number {
    if (value == null || value === '') {
      return -1;
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return -1;
    }
    return numericValue >= 1 ? 1 : 0;
  }

  private persistFilterText(value: string): void {
    if (!this.userId) {
      return;
    }
    this.listStateService.setFilterText(this.userId, value);
  }

  private persistProtocolFilters(values: string[]): void {
    if (!this.userId) {
      return;
    }
    this.listStateService.setProtocolFilters(this.userId, values);
  }

  private applyProtocolFilterSelection(selection: string[]): void {
    const availableCodes = this.currentState.protocolFilterOptions.map((option) => option.code);
    const availableKeys = new Set(
      availableCodes
        .map((code) => this.getProtocolFilterKey(code))
        .filter((code) => code.length > 0),
    );
    const selectionKeys = new Set(
      selection
        .map((code) => this.getProtocolFilterKey(code))
        .filter((code) => availableKeys.has(code)),
    );
    const nextSelection = availableCodes.filter((code) =>
      selectionKeys.has(this.getProtocolFilterKey(code)),
    );

    this.persistProtocolFilters(nextSelection);
    this.patchState({ selectedProtocolFilters: nextSelection, pageIndex: 0 });
    this.recalculateRows();
  }

  private persistColumnVisibility(defs: ColumnDefinition[]): void {
    const visibilityMap = defs.reduce<Record<string, boolean>>((acc, def) => {
      acc[def.def] = def.visible;
      return acc;
    }, {});
    this.listStateService.setColumnVisibilityMap(visibilityMap);
  }

  private filterRowsByRecordType(rows: RecordData[]): RecordData[] {
    if (!this.recordTypeFilter || this.recordTypeFilter.length === 0) {
      return rows;
    }
    const filter = new Set(this.recordTypeFilter);
    return rows.filter((row) => filter.has(normalizeRecordType(row.data?.recordType)));
  }

  private normalizeRecordTypeFilter(filter: string[] | null): string[] | null {
    if (!filter || filter.length === 0) {
      return null;
    }
    const normalized = Array.from(
      new Set(
        filter
          .map((value) => normalizeRecordType(value))
          .filter((value) => value.length > 0),
      ),
    );
    return normalized.length > 0 ? normalized : null;
  }

  private isSameFilter(left: string[] | null, right: string[] | null): boolean {
    if (!left && !right) {
      return true;
    }
    if (!left || !right) {
      return false;
    }
    if (left.length !== right.length) {
      return false;
    }
    return left.every((item, index) => item === right[index]);
  }

  private applyProtocolFilters(
    rows: UnifiedRow[],
    selectedProtocolFilters: string[],
  ): UnifiedRow[] {
    if (!selectedProtocolFilters || selectedProtocolFilters.length === 0) {
      return rows;
    }
    const prefix = UserRecordListViewModel.MEDIA_FILTER_PREFIX;
    const selectedRecordCodes = new Set<string>();
    const selectedMediaMethods = new Set<string>();
    for (const code of selectedProtocolFilters) {
      if (code.startsWith(prefix)) {
        selectedMediaMethods.add(code.slice(prefix.length));
      } else {
        selectedRecordCodes.add(code);
      }
    }
    return rows.filter((row) => {
      if (row.kind === 'media') {
        const method = row.media?.data?.method ?? 'Others';
        return selectedMediaMethods.has(method);
      }
      return selectedRecordCodes.has(normalizeRecordType(row.record?.data?.recordType));
    });
  }

  private resolveSelectedProtocolFilters(availableCodes: string[]): string[] {
    if (!this.userId) {
      return [...availableCodes];
    }

    const persisted = this.listStateService.getProtocolFilters(this.userId);
    if (persisted == null) {
      return [...availableCodes];
    }
    if (persisted.length === 0) {
      return [];
    }

    const availableKeys = new Set(
      availableCodes
        .map((value) => this.getProtocolFilterKey(value))
        .filter((value) => value.length > 0),
    );
    const selectionKeys = new Set(
      persisted
        .map((value) => this.getProtocolFilterKey(value))
        .filter((value) => availableKeys.has(value)),
    );
    const selection = availableCodes.filter((code) =>
      selectionKeys.has(this.getProtocolFilterKey(code)),
    );

    if (selection.length === 0 && availableCodes.length > 0) {
      return [...availableCodes];
    }
    return selection;
  }

  private getProtocolFilterKey(value?: string | null): string {
    const raw = (value ?? '').trim();
    if (!raw) {
      return '';
    }

    const prefix = UserRecordListViewModel.MEDIA_FILTER_PREFIX;
    if (raw.toLowerCase().startsWith(prefix)) {
      const method = raw.slice(prefix.length).trim().toLowerCase();
      return method ? `${prefix}${method}` : '';
    }

    return normalizeRecordType(raw);
  }

  private static readonly MEDIA_FILTER_PREFIX = 'media:';
  private static readonly MEDIA_METHODS = ['VE', 'VF', 'VS', 'Others'] as const;

  private buildProtocolFilterOptions(rows: UnifiedRow[]): ProtocolFilterOption[] {
    const locale = this.protocolLocale;
    const mediaLabel = $localize`:@@home.userRecordList.label.mediaRecord:メディアレコード`;

    // レコード用プロトコル
    const catalogCodes = Array.from(
      new Set(
        RecordTypes
          .map((recordType) => normalizeRecordType(recordType.code))
          .filter((code) => code.length > 0),
      ),
    );
    const recordRowCodes = Array.from(
      new Set(
        rows
          .filter((row) => row.kind === 'record')
          .map((row) => normalizeRecordType(row.record?.data?.recordType))
          .filter((code) => code.length > 0),
      ),
    );
    const catalogCodeSet = new Set(catalogCodes);
    const mergedCodes = [
      ...catalogCodes,
      ...recordRowCodes.filter((code) => !catalogCodeSet.has(code)),
    ];
    const routeScopedFilter = this.recordTypeFilter
      ? new Set(this.recordTypeFilter)
      : null;
    const scopedCodes = routeScopedFilter
      ? mergedCodes.filter((code) => routeScopedFilter.has(code))
      : mergedCodes;

    const recordOptions: ProtocolFilterOption[] = scopedCodes
      .map((code) => {
        const label = getRecordTypePrimaryLabel(code, code, locale);
        const secondaryLabel = this.protocolLocale === 'ja'
          ? getRecordTypeSecondaryLabel(code, label)
          : '';
        return {
          code,
          label,
          secondaryLabel,
          tooltip: secondaryLabel ? `${label} (${secondaryLabel})` : label,
        };
      })
      .sort((left, right) => left.label.localeCompare(right.label));

    // メディア用フィルター（データに存在するメソッドのみ）
    const mediaMethodsInData = new Set(
      rows
        .filter((row) => row.kind === 'media')
        .map((row) => row.media?.data?.method ?? 'Others'),
    );
    const mediaOptions: ProtocolFilterOption[] = UserRecordListViewModel.MEDIA_METHODS
      .filter((method) => mediaMethodsInData.has(method))
      .map((method) => ({
        code: `${UserRecordListViewModel.MEDIA_FILTER_PREFIX}${method}`,
        label: `${mediaLabel} ${method}`,
        secondaryLabel: '',
        tooltip: `${mediaLabel} ${method}`,
      }));

    return [...recordOptions, ...mediaOptions];
  }

  private mergeRecordsAndMedias(
    records: RecordData[],
    rawMedias: any[],
    typedMedias: MediaDocument[],
  ): UnifiedRow[] {
    const TIME_THRESHOLD_MS = 5 * 60 * 1000;
    // レコードID → レコードのマップ（メディア側から逆引き用）
    const recordById = new Map<string, RecordData>();
    for (const rec of records) {
      recordById.set(rec.id, rec);
    }

    const linkedMediaIds = new Set<string>();

    // メディア側の mediaRecordId から逆引き（listRecords APIではレコード側に mediaRecordId が無いため）
    for (const rawMedia of rawMedias) {
      const linkedRecordId: string | undefined = rawMedia?.data?.mediaRecordId;
      if (!linkedRecordId) continue;

      const linkedRecord = recordById.get(linkedRecordId);
      if (!linkedRecord) continue;

      const recType = normalizeRecordType(linkedRecord.data?.recordType);
      if (recType !== 'VS') continue;

      const recTime = getDateTimestamp(linkedRecord.data?.createdAt ?? linkedRecord.data?.createdAtLocalStr);
      const mediaTime = getDateTimestamp(rawMedia?.data?.createdAt);
      if (recTime != null && mediaTime != null && Math.abs(recTime - mediaTime) < TIME_THRESHOLD_MS) {
        linkedMediaIds.add(rawMedia.id);
      }
    }

    const recordRows: UnifiedRow[] = records.map((rec) => ({
      kind: 'record' as const,
      id: rec.id,
      record: rec,
      media: null,
    }));

    const mediaRows: UnifiedRow[] = typedMedias
      .filter((m) => !linkedMediaIds.has(m.id))
      .map((m) => ({
        kind: 'media' as const,
        id: m.id,
        record: null,
        media: m,
      }));

    return [...recordRows, ...mediaRows];
  }

  private buildColumnDefinitions(isAnonymous: boolean): ColumnDefinition[] {
    // 既存の columnDefinitions があれば visible 状態を引き継ぐ
    const existing = this.currentState.columnDefinitions;
    const visibilityMap = new Map(existing.map(cd => [cd.def, cd.visible]));
    const persistedVisibilityMap = this.listStateService.getColumnVisibilityMap();
    const labels = {
      createdAt: $localize`:@@home.userRecordList.column.createdAt:計測日時`,
      recordType: $localize`:@@home.userRecordList.column.recordType:プロトコル`,
      userName: $localize`:@@home.userRecordList.column.userName:ユーザー`,
      measurerName: $localize`:@@home.userRecordList.column.measurerName:スタッフ`,
      numOfSwallowing: $localize`:@@home.userRecordList.column.numOfSwallowing:嚥下回数`,
      meanSwallowingTime: $localize`:@@home.userRecordList.column.meanSwallowingTime:平均嚥下時間`,
      coughPresence: $localize`:@@home.userRecordList.column.coughPresence:むせ`,
      wavDuration: $localize`:@@home.userRecordList.column.wavDuration:記録時間`,
    };

    const defs: ColumnDefinition[] = [
      { def: 'createdAt', label: labels.createdAt, visible: true, locked: true },
      { def: 'recordType', label: labels.recordType, visible: true },
    ];

    if (isAnonymous) {
      defs.push({ def: 'userName', label: labels.userName, visible: true });
    }

    defs.push(
      { def: 'measurerName', label: labels.measurerName, visible: true },
      { def: 'numOfSwallowing', label: labels.numOfSwallowing, visible: false },
      { def: 'meanSwallowingTime', label: labels.meanSwallowingTime, visible: false },
      { def: 'coughPresence', label: labels.coughPresence, visible: false },
      { def: 'wavDuration', label: labels.wavDuration, visible: false },
      { def: 'actions', label: '', visible: true, locked: true },
    );

    // 既存の visible 状態を復元
    return defs.map(cd => ({
      ...cd,
      visible: cd.locked
        ? true
        : visibilityMap.has(cd.def)
          ? visibilityMap.get(cd.def)!
          : persistedVisibilityMap?.[cd.def] ?? cd.visible,
    }));
  }
}
